import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { useInternationalVerify } from "@/utils/useInternationalVerify";

export type AppointmentReminderAttendanceKind =
  | "presencial"
  | "teleatendimento"
  | "chat"
  | "domicilio";

/** Dados resolvidos da consulta para montar o lembrete no WhatsApp Web. */
export interface AppointmentReminderWhatsAppContext {
  patientName: string;
  appointmentStart?: string;
  practitionerName: string;
  healthcareName: string;
  locationLabel: string;
  domicilioAddressLabel?: string;
  virtualServiceLink?: string;
  patientPhone?: string;
  attendanceKind: AppointmentReminderAttendanceKind;
}

function stripDoctorPrefix(name?: string): string {
  const trimmed = (name ?? "").trim();
  if (
    !trimmed ||
    trimmed === "undefined" ||
    trimmed === "Dr. undefined" ||
    trimmed === "Dr."
  ) {
    return "profissional não informado";
  }
  const withoutPrefix = trimmed.replace(/^Dr\.\s*/gi, "").trim();
  if (!withoutPrefix || withoutPrefix === "undefined") {
    return "profissional não informado";
  }
  return withoutPrefix;
}

function formatAppointmentDateTimeLine(start?: string): string {
  if (!start || !dayjs(start).isValid()) {
    return "data não informada";
  }
  const parsed = dayjs(start).locale("pt-br");
  return `${parsed.format("DD/MM/YYYY")} às ${parsed.format("HH:mm")}h`;
}

function formatPractitionerLine(practitionerName: string, healthcareName: string): string {
  return `Dr. ${practitionerName} (${healthcareName})`;
}

function extractHomeVisitAddress(
  appointment: any,
  original: any,
): fhir5.Address | undefined {
  const extensions = original?.extension ?? appointment?.extension ?? [];
  const homeVisitExt = extensions.find((ext: any) =>
    ext.url?.includes("home-visit-address"),
  );
  return homeVisitExt?.valueAddress;
}

function formatHomeVisitAddressLine(address?: fhir5.Address): string {
  if (!address) return "endereço não informado";

  const parts: string[] = [];
  if (address.line?.length) {
    parts.push(address.line.filter(Boolean).join(", "));
  }
  if (address.district) parts.push(address.district);

  const cityState = [address.city, address.state].filter(Boolean).join(" - ");
  if (cityState) parts.push(cityState);

  if (address.postalCode) {
    const cep = address.postalCode.replace(/\D/g, "");
    parts.push(
      cep.length === 8 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : address.postalCode,
    );
  }

  return parts.join(" - ") || "endereço não informado";
}

/**
 * Mesma regra de PatientDetails / ScheduleQueue: chat tem prioridade sobre virtual-service.
 */
export function getAttendanceKindFromLocation(
  locationResource?: fhir5.Location,
): AppointmentReminderAttendanceKind {
  if (!locationResource) return "presencial";

  const channel = locationResource.extension?.find((ext) =>
    ext.url?.includes("attendance-channel"),
  )?.valueCode;

  const isVirtual =
    locationResource.extension?.find((ext) =>
      ext.url?.includes("virtual-service"),
    )?.valueBoolean === true;

  if (channel === "chat") return "chat";
  if (channel === "domicilio") return "domicilio";
  if (isVirtual || channel === "teleatendimento") return "teleatendimento";
  return "presencial";
}

function buildLocationBullets(
  context: AppointmentReminderWhatsAppContext,
): string[] {
  const link = context.virtualServiceLink || "link não disponível";

  switch (context.attendanceKind) {
    case "chat":
      return ["• Local: Chat"];
    case "teleatendimento":
      return [
        "• Local: Teleconsulta",
        `• Link da videoconferência: ${link}`,
      ];
    case "domicilio":
      return [
        "• Local: Atendimento a Domicílio",
        `• Endereço: ${context.domicilioAddressLabel || "endereço não informado"}`,
      ];
    default:
      return [`• Local: ${context.locationLabel}`];
  }
}

/**
 * Normaliza o appointment da fila (extractAppointmentData) + FHIR original
 * para evitar null/undefined na mensagem do lembrete.
 */
export function resolveAppointmentReminderWhatsAppContext(params: {
  appointment: any;
  patient?: fhir5.BundleEntry<fhir5.Patient>;
  locationResource?: fhir5.Location;
  /** Fallback legado quando a Location não está disponível. */
  locationTeleconsulting?: boolean;
  patientPhone?: string;
}): AppointmentReminderWhatsAppContext {
  const {
    appointment,
    patient,
    locationResource,
    locationTeleconsulting,
    patientPhone,
  } = params;
  const original = appointment?.originalData ?? appointment;

  const appointmentStart =
    appointment?.startTime ??
    appointment?.start ??
    original?.start ??
    undefined;

  const patientNameFromBundle = patient?.resource?.name?.find(
    (n) => n.use === "usual" || n.use === "official",
  )?.text;

  const rawPatientName =
    appointment?.patientName ||
    appointment?.patient ||
    patientNameFromBundle ||
    original?.participant?.find((p: any) =>
      p?.actor?.reference?.includes("Patient"),
    )?.actor?.display ||
    "";

  const patientName =
    String(rawPatientName).trim().replace(/^-$/, "") || "Paciente";

  const practitionerFromParticipants = original?.participant?.find((p: any) =>
    p?.actor?.reference?.includes("Practitioner"),
  )?.actor?.display;

  const practitionerRaw =
    appointment?.practitioner || practitionerFromParticipants || "";

  const healthcareFromParticipants = original?.participant?.find((p: any) =>
    p?.actor?.reference?.includes("HealthcareService"),
  )?.actor?.display;

  const healthcareName =
    (appointment?.healthcare || healthcareFromParticipants || "").trim() ||
    "especialidade não informada";

  const virtualServiceLink =
    (typeof appointment?.virtualService === "string"
      ? appointment.virtualService
      : undefined) ||
    original?.virtualService?.[0]?.addressUrl ||
    appointment?.virtualService?.[0]?.addressUrl;

  const locationFromParticipants = original?.participant?.find((p: any) =>
    p?.actor?.reference?.includes("Location"),
  )?.actor?.display;

  const physicalLocationLabel = (
    appointment?.location ||
    appointment?.address ||
    locationFromParticipants ||
    ""
  )
    .trim() || "local não informado";

  const homeVisitAddress = extractHomeVisitAddress(appointment, original);
  const domicilioAddressLabel = formatHomeVisitAddressLine(homeVisitAddress);

  const attendanceKind = locationResource
    ? getAttendanceKindFromLocation(locationResource)
    : locationTeleconsulting
      ? "teleatendimento"
      : "presencial";

  const locationLabel = (() => {
    switch (attendanceKind) {
      case "chat":
        return "Chat";
      case "teleatendimento":
        return "Teleconsulta";
      case "domicilio":
        return "Atendimento a Domicílio";
      default:
        return physicalLocationLabel;
    }
  })();

  return {
    patientName,
    appointmentStart,
    practitionerName: stripDoctorPrefix(practitionerRaw),
    healthcareName,
    locationLabel,
    domicilioAddressLabel:
      attendanceKind === "domicilio" ? domicilioAddressLabel : undefined,
    virtualServiceLink,
    patientPhone: patientPhone?.trim(),
    attendanceKind,
  };
}

export function buildAppointmentReminderMessage(
  context: AppointmentReminderWhatsAppContext,
): string {
  const dateTimeLine = formatAppointmentDateTimeLine(context.appointmentStart);
  const practitionerLine = formatPractitionerLine(
    context.practitionerName,
    context.healthcareName,
  );

  const footer =
    "Para acompanhar seus registros eletrônicos de saúde, agendar novas consultas e aproveitar outros recursos, crie sua conta acessando: https://app.fluxmed.com.br ou no app Fluxmed (Android/iOS).";

  const bulletTopics = [
    `• Profissional: ${practitionerLine}`,
    `• Data e Hora: ${dateTimeLine}`,
    ...buildLocationBullets(context),
  ];

  return [
    `Olá, ${context.patientName}.`,
    "Lembramos que você tem uma consulta agendada",
    ...bulletTopics,
    "",
    footer,
  ].join("\n");
}

export function buildAppointmentReminderWhatsAppUrl(
  context: AppointmentReminderWhatsAppContext,
): string {
  const message = buildAppointmentReminderMessage(context);
  const encodedText = encodeURIComponent(message);
  const rawPhone = context.patientPhone?.trim();

  if (!rawPhone) {
    return `https://web.whatsapp.com/send?text=${encodedText}`;
  }

  const phone = useInternationalVerify.verifyPhoneDDI(rawPhone);
  return `https://web.whatsapp.com/send?phone=+${phone}&text=${encodedText}`;
}
